import axios from "axios";

/**
 * API base URL — migration-ready (Hostinger + Oracle).
 * Set REACT_APP_API_BASE_URL to override. For migration to emergencyqr.in set:
 *   REACT_APP_API_BASE_URL=https://api.emergencyqr.in/v1
 * Current defaults use /v1 path; backend serves both /api and /v1 so existing builds keep working.
 */
const getApiBaseUrl = () => {
  if (process.env.REACT_APP_API_BASE_URL) {
    return process.env.REACT_APP_API_BASE_URL.replace(/\/$/, "");
  }
  if (process.env.NODE_ENV === "production") {
    return "https://api.locket.dityatechnologies.in/v1";
  }
  if (process.env.NODE_ENV === "staging") {
    return "https://api.staging.locket.dityatechnologies.in/v1";
  }
  return "http://localhost:5000/v1";
};

const api = axios.create({
  baseURL: getApiBaseUrl(),
  headers: {
    "Content-Type": "application/json",
  },
});

// Add request interceptor for JWT auth with expiry check
api.interceptors.request.use(
  (config) => {
    // Check for admin token first
    const adminToken = localStorage.getItem("admin_token");
    if (adminToken) {
      config.headers["Authorization"] = `Bearer ${adminToken}`;
      return config;
    }

    // Check for customer token with expiry validation
    const customerToken = localStorage.getItem("locket_jwt");
    const tokenExpiry = localStorage.getItem("locket_jwt_expiry");
    
    if (customerToken && tokenExpiry) {
      const expiryTime = parseInt(tokenExpiry, 10);
      const now = Date.now();
      
      // If token has expired, clear it and don't add to request
      if (now >= expiryTime) {
        localStorage.removeItem("locket_jwt");
        localStorage.removeItem("locket_jwt_expiry");
        // Trigger logout by dispatching a custom event
        window.dispatchEvent(new CustomEvent("tokenExpired"));
      } else {
        config.headers["Authorization"] = `Bearer ${customerToken}`;
      }
    }
    
    return config;
  },
  (error) => Promise.reject(error)
);

// On 401, clear whichever token triggered the request and notify the app
api.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error.response?.status;
    const data = error.response?.data;
    const isRevoked = data?.error === "Token has been revoked";
    const isExpired = data?.code === "TOKEN_EXPIRED" || data?.error === "Token expired";

    if (status === 401 && (isRevoked || isExpired)) {
      // Clear whichever token was active for this request
      if (localStorage.getItem("admin_token")) {
        localStorage.removeItem("admin_token");
        delete api.defaults.headers.common["Authorization"];
        window.dispatchEvent(new CustomEvent("adminTokenExpired"));
      } else {
        localStorage.removeItem("locket_jwt");
        localStorage.removeItem("locket_jwt_expiry");
        window.dispatchEvent(new CustomEvent("tokenExpired"));
      }
    }
    return Promise.reject(error);
  }
);

export default api;
